import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useNotifier } from "../../../Core/Notifier";

export interface PortalCreds {
  username: string;
  password: string;
}

/**
 * One-time display of a newly-created parent's portal login. Rendered on top of
 * the add-parent flow when the backend returns freshly-generated credentials —
 * the temporary password is only ever returned once.
 */
const ParentCredsDialog: React.FC<{
  creds: PortalCreds | null;
  onClose: () => void;
}> = ({ creds, onClose }) => {
  const { showMessage } = useNotifier();
  const loginUrl = `${window.location.origin}/login`;

  const copy = () => {
    if (!creds) return;
    navigator.clipboard
      ?.writeText(
        `Username: ${creds.username}\nPassword: ${creds.password}\nLogin: ${loginUrl}`
      )
      .then(() => showMessage("Credentials copied"))
      .catch(() => {});
  };

  return (
    <Dialog open={!!creds} maxWidth="xs" fullWidth>
      <DialogTitle>Portal Login Details</DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 2 }}>
          Share these with the parent. The password is shown only once.
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gap: 1,
            fontSize: 14,
          }}
        >
          <b>Username:</b>
          <span>{creds?.username}</span>
          <b>Password:</b>
          <span>{creds?.password}</span>
          <b>Login URL:</b>
          <span>{loginUrl}</span>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button startIcon={<ContentCopyIcon />} onClick={copy}>
          Copy
        </Button>
        <Button variant="contained" onClick={onClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ParentCredsDialog;
